import { createAsyncThunk } from '@reduxjs/toolkit';

import { Group } from '../../types/groups';
import { AlgoliaObjectType, getTypeFilters } from '../../utils/algoliaHelper';
import { AppDispatch, RootState } from '../store';
import { TrailMap, fetchGroupsTrails } from '../slices/trails/trails';
import { fetchGroupsAlerts } from '../slices/alerts/alerts';

export type FetchTrailsArgs = {
  primaryAlgoliaIndexId: string;
  alertsAlgoliaIndexId: string;
  groups: Group[];
  defaultTrailFilters?: string;
  trailFilters?: string;
  alertFilters?: string;
  draft?: boolean;
};

type FetchTrailsAndAlertsArgs = FetchTrailsArgs & {
  dispatch: AppDispatch;
  getState: () => RootState;
};

const getTrails = (draft: boolean, getState: () => RootState) => getState().trails[draft ? 'draftTrails' : 'trails'];
const getAlertTrails = (draft: boolean, getState: () => RootState) => getState().trails[draft ? 'draftAlertTrails' : 'alertTrails'];
const getAlerts = (draft: boolean, getState: () => RootState) => getState().alerts[draft ? 'draftAlerts' : 'alerts'];

const fetchFromAlerts = async ({
  dispatch,
  getState,
  primaryAlgoliaIndexId,
  alertsAlgoliaIndexId,
  groups,
  alertFilters,
  draft
}: FetchTrailsAndAlertsArgs): Promise<TrailMap> => {
  await dispatch(fetchGroupsAlerts({ indexId: alertsAlgoliaIndexId, groups, filters: alertFilters, draft }));

  // Since we're only filtering by alerts if no alerts are found then no trails are found
  const alerts = getAlerts(draft, getState);
  if (Object.keys(alerts).length === 0) {
    return {};
  }

  // Query trails filtered by the fetched alerts
  const alertIdFilterString = Object.keys(alerts)
    .map(alertId => `alert_ids:${alertId}`)
    .join(' OR ');
  const trailFilters = `(${alertIdFilterString}) AND ${getTypeFilters([AlgoliaObjectType.Trail])}`;
  await dispatch(fetchGroupsTrails({ indexId: primaryAlgoliaIndexId, groups, filters: trailFilters, draft }));

  // Since we're only filtering by alerts and we fetched trails by matching alerts return all fetched trails
  return getTrails(draft, getState);
};

const fetchFromTrails = async ({
  dispatch,
  getState,
  primaryAlgoliaIndexId,
  alertsAlgoliaIndexId,
  groups,
  trailFilters,
  alertFilters,
  draft
}: FetchTrailsAndAlertsArgs) => {
  await dispatch(fetchGroupsTrails({ indexId: primaryAlgoliaIndexId, groups, filters: trailFilters, draft }));

  const alertTrails = getAlertTrails(draft, getState);

  // If fetched trails have no alerts assigned
  if (Object.keys(alertTrails).length === 0) {
    // If there were alert filters there are no matching trails
    if (alertFilters) {
      return {};
    }

    // Return all trails without fetching alerts
    return getTrails(draft, getState);
  }

  const alertIdFilterString = Object.keys(alertTrails)
    .map(alertId => `objectID:alert-${alertId}`)
    .join(' OR ');
  const filters = alertFilters ? `${alertFilters} AND (${alertIdFilterString})` : alertIdFilterString;
  await dispatch(fetchGroupsAlerts({ indexId: alertsAlgoliaIndexId, groups, filters, draft }));

  // If there are no alert filters return all trails now that we've fetched their alerts
  if (!alertFilters) {
    return getTrails(draft, getState);
  }

  // Only return trails that have a matching alert assigned to them
  const trails: TrailMap = {};
  const alerts = getAlerts(draft, getState);
  const unfilteredTrails = getTrails(draft, getState);
  Object.keys(alerts).forEach(alertId => {
    const trailIds = alertTrails[parseInt(alertId)] || [];
    trailIds.forEach(trailId => {
      trails[trailId] ||= unfilteredTrails[trailId];
    });
  });

  return trails;
};

const fetchTrails = createAsyncThunk<TrailMap, FetchTrailsArgs, { dispatch: AppDispatch; state: RootState }>(
  'filters/fetchTrails',
  async (args: FetchTrailsArgs, { dispatch, getState }) => {
    const { alertFilters, trailFilters, defaultTrailFilters } = args;

    // If there are only active alert filters query alerts first and filter trails by alert results
    if (alertFilters && trailFilters === defaultTrailFilters) {
      return fetchFromAlerts({ ...args, dispatch, getState });
    }

    return fetchFromTrails({ ...args, dispatch, getState });
  }
);

export { fetchTrails };
